// CSV Parser for frontend CSV parsing
// Handles quoted fields, commas, newlines, escaped quotes

export interface ParsedCSV {
  headers: string[];
  rows: Record<string, string>[];
  totalRows: number;
}

/**
 * Parse CSV text into structured data
 * Handles:
 * - Quoted fields with commas
 * - Multi-line fields
 * - Escaped quotes
 * - Various line endings (CRLF, LF, CR)
 */
export const parseCSVText = (text: string): ParsedCSV => {
  const rows: Record<string, string>[] = [];
  const lines: string[] = [];

  // Normalize line endings to LF
  const normalizedText = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Parse lines handling quoted fields that may span multiple lines
  let currentLine = "";
  let inQuotes = false;

  for (let i = 0; i < normalizedText.length; i++) {
    const char = normalizedText[i];
    const nextChar = normalizedText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentLine += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        currentLine += char;
      }
    } else if (char === "\n" && !inQuotes) {
      // End of line (not inside quotes)
      lines.push(currentLine);
      currentLine = "";
    } else {
      currentLine += char;
    }
  }

  // Add last line if exists
  if (currentLine.length > 0 || inQuotes) {
    lines.push(currentLine);
  }

  if (lines.length === 0) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  // Parse header row
  const headerRow = parseCSVLine(lines[0]);
  const headers = headerRow.map((h) => h.trim());

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.length === 0) continue; // Skip empty lines

    const values = parseCSVLine(line);
    const row: Record<string, string> = {};

    headers.forEach((header, index) => {
      row[header] = values[index]?.trim() || "";
    });

    rows.push(row);
  }

  return {
    headers,
    rows,
    totalRows: rows.length,
  };
};

/**
 * Parse a single CSV line into an array of values
 * Handles quoted fields and escaped quotes
 */
const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let currentValue = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentValue += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      // Field separator (not inside quotes)
      values.push(currentValue);
      currentValue = "";
    } else {
      currentValue += char;
    }
  }

  // Add last value
  values.push(currentValue);

  return values;
};

/**
 * Parse CSV file from File object
 */
export const parseCSVFile = async (file: File): Promise<ParsedCSV> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = parseCSVText(text);
        resolve(parsed);
      } catch (error) {
        reject(
          new Error(
            `Failed to parse CSV: ${error instanceof Error ? error.message : "Unknown error"}`
          )
        );
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};

/**
 * Get sample rows from parsed CSV (for preview)
 */
export const getSampleRows = (
  parsedCSV: ParsedCSV,
  limit: number = 20
): Record<string, string>[] => {
  return parsedCSV.rows.slice(0, limit);
};

/**
 * Validate CSV structure
 */
export const validateCSVStructure = (
  parsedCSV: ParsedCSV
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (parsedCSV.headers.length === 0) {
    errors.push("CSV file must have a header row");
  }

  if (parsedCSV.rows.length === 0) {
    errors.push("CSV file must have at least one data row");
  }

  // Check for consistent column count
  const expectedColumns = parsedCSV.headers.length;
  parsedCSV.rows.forEach((row, index) => {
    const actualColumns = Object.keys(row).length;
    if (actualColumns !== expectedColumns) {
      errors.push(`Row ${index + 2} has ${actualColumns} columns, expected ${expectedColumns}`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
